import { Injectable } from '@angular/core';

import { ShSelectedFacetValue } from '../../elements/components/facet/entities/facet.entity';
import { ExtendedFacetProfileField } from '../../core/profiles/entities/extended-profile-field.entity';
import { VirtualFacetField } from '../entities/virtual-facet-field.entity';
import { ShFacetValueTranslationService } from './facet-value-translation.service';

/**
 * Resolves display labels for selected facet values against a facet profile — `facetLabel` from the
 * matching field's `DisplayLabel`, `valueLabel` via the value-translation service. Used by the
 * left-panel facet summary; intended to be reused by the active-filter pill bar so both surfaces
 * render facet labels identically.
 */
@Injectable({ providedIn: 'root' })
export class ShSelectedFacetLabelService {
    constructor(private readonly facetValueTranslateService: ShFacetValueTranslationService) {}

    /**
     * @param virtualFields Descriptors for filters no facet profile lists, such as the Taken panel's
     * "Mijn taken". Without one, such a facet is dropped and an error is logged on every emission.
     */
    public translateSelectedFacetLabels(
        selectedFacets: ShSelectedFacetValue[],
        facetProfileFields: ExtendedFacetProfileField[],
        virtualFields: readonly VirtualFacetField[] = []
    ): ShSelectedFacetValue[] {
        const translatedSelectedFacets: ShSelectedFacetValue[] = [];

        selectedFacets.forEach(selected => {
            const matchingVirtualField: VirtualFacetField | undefined = virtualFields.find(
                virtualField => selected.key === virtualField.key
            );

            if (matchingVirtualField) {
                translatedSelectedFacets.push({
                    ...selected,
                    facetLabel: matchingVirtualField.label,
                    valueLabel:
                        matchingVirtualField.values.find(value => value.value === selected.value)?.label ??
                        selected.value
                });
                return;
            }

            const matchingFacetProfileField = facetProfileFields.find(
                facetProfileField => selected.key === facetProfileField.DottedKey
            );

            if (!matchingFacetProfileField) {
                // Ignore facets with no matching FacetProfileField
                console.error(`Facet with key ${selected.key} not found in Facet Profile`);
                return;
            }

            translatedSelectedFacets.push({
                ...selected,
                // HFBD-6: the same override the panel applies, so a pill and its filter cannot disagree.
                facetLabel: this.facetValueTranslateService.translateLabel(matchingFacetProfileField),
                valueLabel: this.facetValueTranslateService.translateValue(
                    matchingFacetProfileField,
                    selected.value,
                    selected.missing ?? false
                )
            });
        });

        return translatedSelectedFacets;
    }
}
